// ChallengerLocalizationMap - the format expected for sets of ChallengerLocalizations, where the key is the Language String
export type ChallengerLocalizationMap = Map<string, ChallengerLocalization>;

// ChallengerLocalization - contains the strings intended for use by Challenges
export class ChallengerLocalization {
  captchaDescription = "";
  viberPinDescription = "";
  smsPinDescription = "";
  smsEnterPinLinkage = "";
  smsEnterPinNoLinkage = "";
  smsEnterPhone = "";
  smsEnterPhoneInput = "";
  smsEnterPhoneInputError = "";
  smsPinError = "";
  edit = "";
  sendCode = "";
  resendCode = "";
  resendIn = "";
  enterCode = "";
  ttlExpired = "";
  smsHelpMessage = "";
  phoneHelpMessage = "";
  phoneBlockedMessage = "";
  securityCheckRequired = "";
  imNotaBot = "";
  imNotaBotExplanation = "";
  imNotaBotRequired = "";
}

// DefaultLocalizationMap - the default map of ChallengerLocalizations offered by the Challenger Team
export const DefaultLocalizationMap: ChallengerLocalizationMap = new Map<string, ChallengerLocalization>([
  ["de-DE", {
    captchaDescription: "Bitte geben Sie den oben angezeigten Text ein",
    viberPinDescription: "Bitte geben Sie 6 Ziffern aus der Viber-Nachricht ein",
    smsPinDescription: "Bitte geben Sie den Authentifizierungscode ein",
    smsEnterPinLinkage: "Der Authentifizierungscode wird an die folgende Telefonnummer gesendet. Bitte geben Sie den „Authentifizierungscode“ ein, den Sie erhalten haben, und fahren Sie fort",
    smsEnterPinNoLinkage: "Der Authentifizierungscode wird an die folgende Telefonnummer gesendet. Bitte geben Sie den „Authentifizierungscode“ ein, den Sie erhalten haben, und fahren Sie fort",
    smsEnterPhone: "Bitte geben Sie eine Telefonnummer ein, unter der Sie den Authentifizierungscode empfangen können, und klicken Sie auf die Schaltfläche [Authentifizierungscode anfordern]",
    smsEnterPhoneInput: "Bitte geben Sie Ihre Telefonnummer ein",
    smsEnterPhoneInputError: "Bitte geben Sie das korrekte Telefonnummer ein",
    smsPinError: "Authentifizierungscode ist abgelaufen. Bitte fordern Sie ihn erneut an",
    edit: "bearbeiten",
    sendCode: "Authentifizierungscode anfordern",
    resendCode: "Authentifizierungscode anfordern",
    resendIn: "Ein neuer Code kann in {0} Sekunden angefordert werden",
    enterCode: "Code eingeben",
    ttlExpired: "Ihre Sitzung ist abgelaufen",
    smsHelpMessage: "Falls Sie keinen Authentifizierungscode auf Ihrem Mobiltelefon empfangen haben, fordern Sie bitte über die Schaltfläche unten erneut einen an",
    phoneHelpMessage: "Bitte geben Sie Ihre Telefonnummer ohne Bindestrich (-) ein",
    phoneBlockedMessage: "Der Zugriff ist nicht autorisiert",
    securityCheckRequired: "Sicherheitsüberprüfung (erforderlich)",
    imNotaBot: "Ich bin kein Roboter",
    imNotaBotExplanation: "Um Betrug vorzubeugen, wird hiermit überprüft, ob Sie (und kein Roboter) Anfragen senden.",
    imNotaBotRequired: "Darf nicht leer sein.",
  }],
  ["en-US", {
    captchaDescription: "Please enter the text shown above",
    viberPinDescription: "Please enter 6 digits from Viber message",
    smsPinDescription: "Please enter the authentication code",
    smsEnterPinLinkage: "Authentication code is sent to the following phone number. Please enter the \"authentication code\" you received and proceed",
    smsEnterPinNoLinkage: "Authentication code is sent to the following phone number. Please enter the \"authentication code\" you received and proceed",
    smsEnterPhone: "Please enter a phone number that can receive the authentication code, and click on [Send authentication code] button",
    smsEnterPhoneInput: "Please enter your phone number",
    smsEnterPhoneInputError: "Please input correct phone",
    smsPinError: "Authentication code is expired. Please request it again",
    edit: "edit",
    sendCode: "Send authentication code",
    resendCode: "Send authentication code",
    resendIn: "Can request a new code in {0} seconds",
    enterCode: "Enter code",
    ttlExpired: "Your session has expired",
    smsHelpMessage: "If you did not receive an authentication code on your mobile phone, please request again by using the button below",
    phoneHelpMessage: "Please enter your phone number without hyphen (-)",
    phoneBlockedMessage: "Access is unauthorized",
    securityCheckRequired: "Security check (Required)",
    imNotaBot: "I'm not a robot",
    imNotaBotExplanation: "To prevent fraud, this is to verify that you (and not a robot) are sending requests.",
    imNotaBotRequired: "Cannot be blank.",
  }],
  ["es-ES", {
    captchaDescription: "Por favor ingrese el texto arriba",
    viberPinDescription: "Ingrese 6 dígitos del mensaje de Viber",
    smsPinDescription: "Introduce el código de autentificación",
    smsEnterPinLinkage: "El código de autenticación se envía al siguiente número de teléfono. Ingrese el \"código de autenticación\" que recibió y continúe",
    smsEnterPinNoLinkage: "El código de autenticación se envía al siguiente número de teléfono. Ingrese el \"código de autenticación\" que recibió y continúe",
    smsEnterPhone: "Introduce un número de teléfono que pueda recibir el código de autentificación",
    smsEnterPhoneInput: "Introduce tu número de teléfono",
    smsEnterPhoneInputError: "Por favor ingrese el teléfono correcto",
    smsPinError: "El código de autentificación ha caducado",
    edit: "editar",
    sendCode: "Solicitar un código de autentificación",
    resendCode: "Solicitar un código de autentificación",
    resendIn: "Puedes solicitar un código nuevo en {0} segundos",
    enterCode: "Introduzca el código",
    ttlExpired: "Tu sesión ha caducado",
    smsHelpMessage: "Si no recibiste un código de autentificación en tu teléfono móvil, solicítalo de nuevo mediante el siguiente botón",
    phoneHelpMessage: "Introduce tu número de teléfono sin guión (-)",
    phoneBlockedMessage: "El acceso no está autorizado",
    securityCheckRequired: "Control de seguridad (Requerido)",
    imNotaBot: "No soy un robot",
    imNotaBotExplanation: "Para evitar el fraude, esto es para verificar que usted (y no un robot) está enviando solicitudes.",
    imNotaBotRequired: "No puede estar en blanco.",
  }],
  ["fr-FR", {
    captchaDescription: "Entrez le texte ci-dessus",
    viberPinDescription: "Veuillez saisir le code d'authentification",
    smsPinDescription: "Veuillez saisir le code d'authentification",
    smsEnterPinLinkage: "Le code d'authentification est envoyé au numéro de téléphone suivant. Veuillez saisir le \"code d'authentification\" que vous avez reçu et continuer",
    smsEnterPinNoLinkage: "Le code d'authentification est envoyé au numéro de téléphone suivant. Veuillez saisir le \"code d'authentification\" que vous avez reçu et continuer",
    smsEnterPhone: "Veuillez saisir un numéro de téléphone pouvant recevoir le code d'authentification, et cliquer sur le bouton [Envoyer un code d'authentification]",
    smsEnterPhoneInput: "Veuillez saisir votre numéro de téléphone",
    smsEnterPhoneInputError: "Le numéro de téléphone est incorrect",
    smsPinError: "Le code d'authentification a expiré. Veuillez en demander un à nouveau.",
    edit: "modifier",
    sendCode: "Envoyer un code d'authentification",
    resendCode: "Envoyer un code d'authentification",
    resendIn: "Demande d'un nouveau code possible dans {0} secondes",
    enterCode: "Entrez le code",
    ttlExpired: "Votre session a expiré",
    smsHelpMessage: "Si vous n'avez pas reçu le code d'authentification sur votre téléphone mobile, veuillez en demander un à nouveau à l'aide du bouton ci-dessous",
    phoneHelpMessage: "Veuillez saisir votre numéro de téléphone sans tiret (-)",
    phoneBlockedMessage: "Accès non autorisé",
    securityCheckRequired: "Contrôle de sécurité (obligatoire)",
    imNotaBot: "Je ne suis pas un robot",
    imNotaBotExplanation: "Pour éviter la fraude, il s'agit de vérifier que vous (et non un robot) envoyez des demandes.",
    imNotaBotRequired: "Ne peut être vide.",
  }],
  ["id-ID", {
    captchaDescription: "Masukkan teks yang ditampilkan di atas",
    viberPinDescription: "Masukkan 6 digit dari pesan Viber",
    smsPinDescription: "Masukkan 6 digit dari pesan SMS",
    smsEnterPinLinkage: "Kode otentikasi dikirim ke nomor telepon berikut. Masukkan \"kode autentikasi\" yang Anda terima dan lanjutkan",
    smsEnterPinNoLinkage: "Kode otentikasi dikirim ke nomor telepon berikut. Masukkan \"kode autentikasi\" yang Anda terima dan lanjutkan",
    smsEnterPhone: "Silakan masukkan nomor telepon yang dapat menerima kode otentikasi, dan klik tombol [Kirim kode otentikasi]",
    smsEnterPhoneInput: "Masukkan nomor telepon anda",
    smsEnterPhoneInputError: "Harap masukkan telepon yang benar",
    smsPinError: "Kode autentikasi kedaluwarsa. Silakan memintanya lagi",
    edit: "sunting",
    sendCode: "Kirim kode otentikasi",
    resendCode: "Kirim kode otentikasi",
    resendIn: "Dapat meminta kode baru dalam {0} detik",
    enterCode: "Memasukkan kode",
    ttlExpired: "Sesi Anda telah berakhir",
    smsHelpMessage: "Jika Anda tidak menerima kode autentikasi di ponsel Anda, silakan minta lagi dengan menggunakan tombol di bawah",
    phoneHelpMessage: "Silakan masukkan nomor telepon Anda tanpa tanda hubung (-)",
    phoneBlockedMessage: "Akses tidak sah",
    securityCheckRequired: "Pemeriksaan keamanan (Wajib)",
    imNotaBot: "Saya bukan robot",
    imNotaBotExplanation: "Untuk mencegah penipuan, ini untuk memverifikasi bahwa Anda (dan bukan robot) yang mengirimkan permintaan.",
    imNotaBotRequired: "Tidak bisa kosong.",
  }],
  ["it-IT", {
    captchaDescription: "Inserisci il testo mostrato sopra",
    viberPinDescription: "Inserisci 6 cifre dal messaggio Viber",
    smsPinDescription: "Inserisci 6 cifre dal messaggio SMS",
    smsEnterPinLinkage: "Il codice di autenticazione viene inviato al seguente numero di telefono. Inserisci il \"codice di autenticazione\" che hai ricevuto e procedi",
    smsEnterPinNoLinkage: "Il codice di autenticazione viene inviato al seguente numero di telefono. Inserisci il \"codice di autenticazione\" che hai ricevuto e procedi",
    smsEnterPhone: "Inserisci un numero di telefono che può ricevere il codice di autenticazione e fai clic sul pulsante [Invia codice di autenticazione]",
    smsEnterPhoneInput: "Per favore immetti il ​​tuo numero di telefono",
    smsEnterPhoneInputError: "Inserisci il telefono corretto",
    smsPinError: "Il codice di autenticazione è scaduto. Si prega di richiederlo nuovamente",
    edit: "modificare",
    sendCode: "Invia codice di autenticazione",
    resendCode: "Invia codice di autenticazione",
    resendIn: "Può richiedere un nuovo codice in {0} secondi",
    enterCode: "Inserisci il codice",
    ttlExpired: "La tua sessione è scaduta",
    smsHelpMessage: "Se non hai ricevuto un codice di autenticazione sul tuo cellulare, richiedilo nuovamente utilizzando il pulsante qui sotto",
    phoneHelpMessage: "Inserisci il tuo numero di telefono senza trattino (-)",
    phoneBlockedMessage: "L'accesso non è autorizzato",
    securityCheckRequired: "Controllo di sicurezza (obbligatorio)",
    imNotaBot: "Non sono un robot",
    imNotaBotExplanation: "Per prevenire le frodi, questo serve per verificare che tu (e non un robot) stia inviando richieste.",
    imNotaBotRequired: "Non può essere vuoto.",
  }],
  ["ja-JP", {
    captchaDescription: "上記のテキストを入力してください",
    viberPinDescription: "Viberメッセージから6桁を入力してください。",
    smsPinDescription: "認証コードをご入力ください。",
    smsEnterPinLinkage: "以下の携帯電話番号に認証コードを送信いたしました。届いた「認証コード」を入力し、次の手続きにお進みください。",
    smsEnterPinNoLinkage: "以下の携帯電話番号に認証コードを送信いたしました。届いた「認証コード」を入力し、次の手続きにお進みください。",
    smsEnterPhone: "認証コードを受信できる携帯電話番号を入力して「認証コードを送信」を押してください。",
    smsEnterPhoneInput: "電話番号をご入力ください。",
    smsEnterPhoneInputError: "正しい電話番号をご入力ください。",
    smsPinError: "認証コードの使用期限が切れました。再度認証コードを送信してください。",
    edit: "変更",
    sendCode: "認証コードを送信",
    resendCode: "認証コードの送信",
    resendIn: "{0}秒後に再度送信が可能になります。",
    enterCode: "認証コード",
    ttlExpired: "セッションの有効期限が切れています。",
    smsHelpMessage: "携帯電話に認証コードが届かない場合は以下のボタンから再度送信してください。",
    phoneHelpMessage: "※ハイフン(-)なしでご入力ください。",
    phoneBlockedMessage: "不正なアクセスが疑われたため処理を中断いたしました。",
    securityCheckRequired: "セキュリティチェック（必須）",
    imNotaBot: "ロボットによる操作ではありません",
    imNotaBotExplanation: "不正対策のために、画面の操作がロボットではなく、お客様ご自身によるものであることを確認する項目です。",
    imNotaBotRequired: "必須項目です。",
  }],
  ["nl-NL", {
    captchaDescription: "Vul de bovenstaande tekst",
    viberPinDescription: "Voer 6 cijfers van het Viber-bericht in",
    smsPinDescription: "Voer de authenticatiecode in",
    smsEnterPinLinkage: "Verificatiecode wordt verzonden naar het volgende telefoonnummer. Voer de \"authenticatiecode\" in die u heeft ontvangen en ga verder",
    smsEnterPinNoLinkage: "Verificatiecode wordt verzonden naar het volgende telefoonnummer. Voer de \"authenticatiecode\" in die u heeft ontvangen en ga verder",
    smsEnterPhone: "Voer een telefoonnummer in dat de authenticatiecode kan ontvangen en klik op de knop [Verzend authenticatiecode]",
    smsEnterPhoneInput: "Voer uw telefoonnummer in",
    smsEnterPhoneInputError: "Voer de juiste telefoon in",
    smsPinError: "Authenticatiecode is verlopen. Vraag het opnieuw aan",
    edit: "bewerking",
    sendCode: "Authenticatiecode verzenden",
    resendCode: "Authenticatiecode verzenden",
    resendIn: "Kan binnen {0} seconden een nieuwe code aanvragen",
    enterCode: "Voer code in",
    ttlExpired: "Uw sessie is verlopen",
    smsHelpMessage: "Indien u geen authenticatiecode op uw mobiele telefoon heeft ontvangen, kunt u deze via onderstaande knop opnieuw aanvragen",
    phoneHelpMessage: "Voer uw telefoonnummer in zonder koppelteken (-)",
    phoneBlockedMessage: "Toegang is ongeautoriseerd",
    securityCheckRequired: "Beveiligingscontrole (verplicht)",
    imNotaBot: "Ik ben geen robot",
    imNotaBotExplanation: "Om fraude te voorkomen, is dit om te verifiëren dat u (en niet een robot) verzoeken verzendt.",
    imNotaBotRequired: "Kan niet leeg zijn",
  }],
  ["pt-PT", {
    captchaDescription: "Digite o texto mostrado acima",
    viberPinDescription: "Insira 6 dígitos da mensagem do Viber",
    smsPinDescription: "Por favor insira o código de autenticação",
    smsEnterPinLinkage: "O código de autenticação é enviado para o seguinte número de telefone. Digite o \"código de autenticação\" que você recebeu e prossiga",
    smsEnterPinNoLinkage: "O código de autenticação é enviado para o seguinte número de telefone. Digite o \"código de autenticação\" que você recebeu e prossiga",
    smsEnterPhone: "Insira um número de telefone que possa receber o código de autenticação e clique no botão [Enviar código de autenticação]",
    smsEnterPhoneInput: "Por favor, digite seu número de telefone",
    smsEnterPhoneInputError: "Por favor insira o telefone correto",
    smsPinError: "O código de autenticação expirou. Por favor, solicite novamente",
    edit: "editar",
    sendCode: "Enviar código de autenticação",
    resendCode: "Enviar código de autenticação",
    resendIn: "Pode solicitar um novo código em {0} segundos",
    enterCode: "Coloque o código",
    ttlExpired: "Sua sessão expirou",
    smsHelpMessage: "Se você não recebeu um código de autenticação em seu celular, solicite novamente usando o botão abaixo",
    phoneHelpMessage: "Por favor, digite seu número de telefone sem hífen (-)",
    phoneBlockedMessage: "Acesso não autorizado",
    securityCheckRequired: "Verificação de segurança (obrigatório)",
    imNotaBot: "Não sou um robô",
    imNotaBotExplanation: "Para evitar fraudes, isso serve para verificar se você (e não um robô) está enviando solicitações.",
    imNotaBotRequired: "Nāo pode estar em branco.",
  }],
  ["th-TH", {
    captchaDescription: "โปรดป้อนข้อความที่แสดงด้านบน",
    viberPinDescription: "โปรดป้อนตัวเลข 6 หลักจากข้อความ Viber",
    smsPinDescription: "โปรดป้อนตัวเลข 6 หลักจากข้อความ SMS",
    smsEnterPinLinkage: "รหัสยืนยันตัวตนจะถูกส่งไปยังหมายเลขโทรศัพท์ต่อไปนี้ โปรดป้อน \"รหัสการตรวจสอบสิทธิ์\" ที่คุณได้รับและดำเนินการต่อ",
    smsEnterPinNoLinkage: "รหัสยืนยันตัวตนจะถูกส่งไปยังหมายเลขโทรศัพท์ต่อไปนี้ โปรดป้อน \"รหัสการตรวจสอบสิทธิ์\" ที่คุณได้รับและดำเนินการต่อ",
    smsEnterPhone: "โปรดป้อนหมายเลขโทรศัพท์ที่สามารถรับรหัสยืนยันตัวตนได้ และคลิกที่ปุ่ม [ส่งรหัสยืนยันตัวตน]",
    smsEnterPhoneInput: "กรุณากรอกหมายเลขโทรศัพท์ของคุณ",
    smsEnterPhoneInputError: "กรุณาใส่หมายเลขโทรศัพท์ที่ถูกต้อง",
    smsPinError: "รหัสยืนยันตัวตนหมดอายุแล้ว กรุณาขออีกครั้ง",
    edit: "แก้ไข",
    sendCode: "ส่งรหัสยืนยันตัวตน",
    resendCode: "ส่งรหัสยืนยันตัวตน",
    resendIn: "สามารถขอรหัสใหม่ได้ภายใน {0} วินาที",
    enterCode: "ใส่รหัส",
    ttlExpired: "เซสชันของคุณหมดอายุแล้ว",
    smsHelpMessage: "หากคุณไม่ได้รับรหัสยืนยันตัวตนบนโทรศัพท์มือถือของคุณ โปรดขออีกครั้งโดยใช้ปุ่มด้านล่าง",
    phoneHelpMessage: "กรุณากรอกหมายเลขโทรศัพท์ของคุณโดยไม่ต้องใส่ยัติภังค์ (-)",
    phoneBlockedMessage: "การเข้าถึงไม่ได้รับอนุญาต",
    securityCheckRequired: "ตรวจสอบความปลอดภัย (จำเป็น)",
    imNotaBot: "ฉันไม่ใช่หุ่นยนต์",
    imNotaBotExplanation: "เพื่อป้องกันการฉ้อโกง นี่คือการตรวจสอบว่าคุณ (ไม่ใช่หุ่นยนต์) กำลังส่งคำขอ",
    imNotaBotRequired: "ไม่สามารถเว้นว่างได้",
  }],
  ["zh-CN", {
    captchaDescription: "请输入上面显示的文字",
    viberPinDescription: "请从Viber消息输入6位数字",
    smsPinDescription: "请输入验证码",
    smsEnterPinLinkage: "验证码发送至以下电话号码。 请输入您收到的“验证码”并继续",
    smsEnterPinNoLinkage: "验证码发送至以下电话号码。 请输入您收到的“验证码”并继续",
    smsEnterPhone: "请输入可以接收验证码的电话号码，然后点击[发送验证码]按钮。",
    smsEnterPhoneInput: "请输入您的手机号码",
    smsEnterPhoneInputError: "请输入正确的手机",
    smsPinError: "验证码已过期",
    edit: "编辑",
    sendCode: "发送验证码",
    resendCode: "发送验证码",
    resendIn: "可在 {0} 秒后申请新的验证码",
    enterCode: "输入代码",
    ttlExpired: "您的会话已过期",
    smsHelpMessage: "如果您的手机没有收到验证码，请使用下面的按钮重新申请",
    phoneHelpMessage: "请输入您的电话号码，不要使用连字符 (-)",
    phoneBlockedMessage: "访问未经授权",
    securityCheckRequired: "安全检查 (必填)",
    imNotaBot: "我不是机器人",
    imNotaBotExplanation: "这是为了验证屏幕操作是由您自己而不是机器人执行的反欺诈措施。",
    imNotaBotRequired: "不能为空白。",
  }],
  ["zh-TW", {
    captchaDescription: "請輸入上面顯示的文字",
    viberPinDescription: "請從Viber消息輸入6位數字",
    smsPinDescription: "請輸入認證碼",
    smsEnterPinLinkage: "驗證碼發送至以下電話號碼。 請輸入您收到的“驗證碼”並繼續",
    smsEnterPinNoLinkage: "驗證碼發送至以下電話號碼。 請輸入您收到的“驗證碼”並繼續",
    smsEnterPhone: "請輸入可以接收認證碼的電話號碼， 並點選 [發送認證碼] 按鈕",
    smsEnterPhoneInput: "請輸入您的電話號碼",
    smsEnterPhoneInputError: "請輸入正確的手機",
    smsPinError: "認證碼已過期。請再次請求",
    edit: "編輯",
    sendCode: "發送認證碼",
    resendCode: "發送認證碼",
    resendIn: "可以在 {0} 秒內請求一個新的代碼",
    enterCode: "輸入代碼",
    ttlExpired: "您的使用期限已過",
    smsHelpMessage: "如果沒有在您的手機收到認證碼，請使用下面的按鈕重新請求",
    phoneHelpMessage: "請輸入您的電話號碼，不要使用連字號 (-)",
    phoneBlockedMessage: "訪問未經授權",
    securityCheckRequired: "安全檢查（必填）",
    imNotaBot: "我不是機器人",
    imNotaBotExplanation: "這是為了驗證螢幕操作是由您自己而不是機器人執行的反詐騙措施。",
    imNotaBotRequired: "不能為空白。",
  }],
  ["uk-UA", {
    captchaDescription: "будь ласка, введіть текст, показаний вище",
    viberPinDescription: "Будь ласка, введіть 6 цифр із повідомлення Viber",
    smsPinDescription: "Будь ласка, введіть код автентифікації",
    smsEnterPinLinkage: "Код автентифікації надіслано на наступний номер телефону. Будь ласка, введіть \"код автентифікації\", який ви отримали, і продовжуйте",
    smsEnterPinNoLinkage: "Код автентифікації надіслано на наступний номер телефону. Будь ласка, введіть \"код автентифікації\", який ви отримали, і продовжуйте",
    smsEnterPhone: "Будь ласка, введіть номер телефону, на який можна отримати код автентифікації, і натисніть кнопку [Надіслати код автентифікації]",
    smsEnterPhoneInput: "Будь ласка, введіть свій номер телефону",
    smsEnterPhoneInputError: "Введіть правильний телефон",
    smsPinError: "Термін дії коду автентифікації минув. Будь ласка, подайте запит ще раз",
    edit: "редагувати",
    sendCode: "Надіслати код автентифікації",
    resendCode: "Надіслати код автентифікації",
    resendIn: "Можна подати запит на новий код через {0} секунд",
    enterCode: "Введіть код",
    ttlExpired: "Ваш сеанс закінчився",
    smsHelpMessage: "Якщо ви не отримали код автентифікації на свій мобільний телефон, надішліть запит ще раз, натиснувши кнопку нижче",
    phoneHelpMessage: "Введіть свій номер телефону без дефіса (-)",
    phoneBlockedMessage: "Доступ несанкціонований",
    securityCheckRequired: "Перевірка безпеки (обов'язково)",
    imNotaBot: "Я не робот",
    imNotaBotExplanation: "Щоб запобігти шахрайству, це означає, що ви (а не робот) надсилаєте запити.",
    imNotaBotRequired: "Не може бути пустим.",
  }],
  ["kr-KR", {
    captchaDescription: "위에 표시된 문구를 입력하십시오",
    viberPinDescription: "Viber 메시지의 6자리 숫자를 입력하세요",
    smsPinDescription: "인증번호를 입력해주세요",
    smsEnterPinLinkage: "인증번호는 아래 전화번호로 발송됩니다. 받으신 \"인증코드\"를 입력하시고 진행해주세요",
    smsEnterPinNoLinkage: "인증번호는 아래 전화번호로 발송됩니다. 받으신 \"인증코드\"를 입력하시고 진행해주세요",
    smsEnterPhone: "인증코드를 받을 수 있는 전화번호를 입력하신 후, [인증코드 전송] 버튼을 클릭해주세요",
    smsEnterPhoneInput: "전화번호를 입력해주세요",
    smsEnterPhoneInputError: "올바른 전화번호를 입력해주세요",
    smsPinError: "인증코드가 만료되었습니다. 다시 요청해주세요",
    edit: "편집하다",
    sendCode: "인증코드 보내기",
    resendCode: "인증코드 보내기",
    resendIn: "{0}초 안에 새 코드를 요청할 수 있습니다",
    enterCode: "코드를 입력",
    ttlExpired: "기간이 만료되었습니다",
    smsHelpMessage: "휴대폰으로 인증번호를 받지 못한 경우, 아래 버튼을 통해 다시 요청해주세요",
    phoneHelpMessage: "하이픈(-) 없이 전화번호를 입력해주세요",
    phoneBlockedMessage: "액세스 권한이 없습니다",
    securityCheckRequired: "보안검색(필수)",
    imNotaBot: "로봇이 아닙니다",
    imNotaBotExplanation: "부정이용을 방지하기 위해 로봇이 아닌 귀하가 요청을 보내고 있는지 확인하는 항목입니다.",
    imNotaBotRequired: "필수 항목입니다.",
  }],
  ["ms-MY", {
    captchaDescription: "Sila masukkan teks yang ditunjukkan di atas",
    viberPinDescription: "Sila masukkan 6 digit daripada mesej Viber",
    smsPinDescription: "Sila masukkan kod pengesahan",
    smsEnterPinLinkage: "Kod pengesahan dihantar ke nombor telefon berikut. Sila masukkan \"kod pengesahan\" yang anda terima dan teruskan",
    smsEnterPinNoLinkage: "Kod pengesahan dihantar ke nombor telefon berikut. Sila masukkan \"kod pengesahan\" yang anda terima dan teruskan",
    smsEnterPhone: "Sila masukkan nombor telefon yang boleh menerima kod pengesahan, dan klik pada butang [Hantar kod pengesahan]",
    smsEnterPhoneInput: "Sila masukkan nombor telefon anda",
    smsEnterPhoneInputError: "Sila masukkan telefon yang betul",
    smsPinError: "Kod pengesahan tamat tempoh. Sila minta sekali lagi",
    edit: "menyunting",
    sendCode: "Hantar kod pengesahan",
    resendCode: "Hantar kod pengesahan",
    resendIn: "Boleh meminta kod baharu dalam {0} saat",
    enterCode: "Masukkan kod",
    ttlExpired: "Sesi anda telah tamat tempoh",
    smsHelpMessage: "Jika anda tidak menerima kod pengesahan pada telefon mudah alih anda, sila minta sekali lagi dengan menggunakan butang di bawah",
    phoneHelpMessage: "Sila masukkan nombor telefon anda tanpa tanda sempang (-)",
    phoneBlockedMessage: "Akses tidak dibenarkan",
    securityCheckRequired: "Pemeriksaan keselamatan (Diperlukan)",
    imNotaBot: "Saya bukan robot",
    imNotaBotExplanation: "Untuk mengelakkan penipuan, ini adalah untuk mengesahkan bahawa anda (dan bukan robot) menghantar permintaan.",
    imNotaBotRequired: "Tidak boleh kosong.",
  }],
  ["tl-PH", {
    captchaDescription: "Pakilagay ang tekstong ipinapakita sa itaas",
    viberPinDescription: "Mangyaring magpasok ng 6 na digit mula sa mensahe ng Viber",
    smsPinDescription: "Pakilagay ang authentication code",
    smsEnterPinLinkage: "Ang authentication code ay ipinadala sa sumusunod na numero ng telepono. Pakipasok ang \"authentication code\" na iyong natanggap at magpatuloy",
    smsEnterPinNoLinkage: "Ang authentication code ay ipinadala sa sumusunod na numero ng telepono. Pakipasok ang \"authentication code\" na iyong natanggap at magpatuloy",
    smsEnterPhone: "Mangyaring magpasok ng numero ng telepono na maaaring makatanggap ng authentication code, at mag-click sa [Ipadala ang authentication code] na buton",
    smsEnterPhoneInput: "Pakibigay na lng po ng phone number niyo",
    smsEnterPhoneInputError: "Mangyaring ipasok ang tamang telepono",
    smsPinError: "Ang authentication code ay nag-expire na. Mangyaring humiling muli",
    edit: "mamatnugot",
    sendCode: "Magpadala ng authentication code",
    resendCode: "Magpadala ng authentication code",
    resendIn: "Maaaring humiling ng bagong code sa {0} (na) segundo",
    enterCode: "Ipasok ang code",
    ttlExpired: "Nag-expire na ang iyong session",
    smsHelpMessage: "Kung hindi ka nakatanggap ng authentication code sa iyong mobile phone, mangyaring humiling muli sa pamamagitan ng paggamit ng button sa ibaba",
    phoneHelpMessage: "Mangyaring ipasok ang iyong numero ng telepono nang walang gitling (-)",
    phoneBlockedMessage: "Ang pag-access ay hindi awtorisado",
    securityCheckRequired: "Pagsusuri sa seguridad (Kinakailangan)",
    imNotaBot: "Hindi ako robot",
    imNotaBotExplanation: "Para maiwasan ang panloloko, kailangan ito para ma-verify na ikaw (at hindi isang robot) ang nagpapadala ng mga request.",
    imNotaBotRequired: "Hindi pwedeng blangko.",
  }],
  ["vn-VN", {
    captchaDescription: "Vui lòng nhập văn bản hiển thị ở trên",
    viberPinDescription: "Vui lòng nhập 6 chữ số từ tin nhắn Viber",
    smsPinDescription: "Vui lòng nhập mã xác thực",
    smsEnterPinLinkage: "Mã xác thực được gửi đến số điện thoại sau. Vui lòng nhập \"mã xác thực\" bạn nhận được và tiếp tục",
    smsEnterPinNoLinkage: "Mã xác thực được gửi đến số điện thoại sau. Vui lòng nhập \"mã xác thực\" bạn nhận được và tiếp tục",
    smsEnterPhone: "Vui lòng nhập số điện thoại có thể nhận mã xác thực và bấm vào nút [Gửi mã xác thực]",
    smsEnterPhoneInput: "Xin vui lòng điền số điện thoại của bạn",
    smsEnterPhoneInputError: "Please input correct phone",
    smsPinError: "Authentication code is expired. Please request it again",
    edit: "biên tập",
    sendCode: "Gửi mã xác thực",
    resendCode: "Gửi mã xác thực",
    resendIn: "Có thể yêu cầu mã mới sau {0} giây",
    enterCode: "Nhập mã",
    ttlExpired: "Phiên của bạn đã hết hạn",
    smsHelpMessage: "Nếu bạn không nhận được mã xác thực trên điện thoại di động của mình, vui lòng yêu cầu lại bằng cách sử dụng nút bên dưới",
    phoneHelpMessage: "Vui lòng nhập số điện thoại của bạn không dấu gạch ngang (-)",
    phoneBlockedMessage: "Truy cập trái phép",
    securityCheckRequired: "Kiểm tra an ninh (Bắt buộc)",
    imNotaBot: "Tôi không phải người máy",
    imNotaBotExplanation: "Để ngăn chặn gian lận, điều này nhằm xác minh rằng bạn (chứ không phải rô-bốt) đang gửi yêu cầu.",
    imNotaBotRequired: "Không thể chừa trống.",
  }]
]);
